import React, { useState, useEffect, useMemo } from 'react';
import { 
  Backdrop,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  List,
  Paper,
  Rating,
  Skeleton,
  styled,
  TextField,
  ThemeProvider,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  ListItem,
  ListItemText 
} from '@mui/material';
import { lightTheme, darkTheme } from '../../Utils/baseTheme';
import HomeHeader from '../../Components/HomeHeader';
import PlaceIcon from '@mui/icons-material/Place';
import PlatformCard from './Components/platformCard';
import TikTokIcon from '../../Components/Icons/TikTok';
import { useNavigate, useParams } from 'react-router-dom';
import StatsCard from './Components/statsCard';
import { generateRainbowColors, getPlatformEngagement, mapPlatformsToAudience, mapPlatformsToEngagement, platformRatesListFromCreator, platforms, platformToFollowing } from '../../Utils/constants';
import { useQuery } from 'react-query';
import API from '../../API';
import CountryCard from './Components/countryCard';
import GenderCard from './Components/genderCard';
import AgeCard from './Components/ageCard';
import PageTitle from '../../Components/PageTitle';
import BookingDialog from '../Misc/bookingdialog';
import LeaveReview from '../Dashboard/Creators/leaveReview';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { 
  Instagram as InstagramIcon,
  YouTube as YoutubeIcon,
  Twitter as TwitterIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import PodcastsIcon from '@mui/icons-material/Podcasts';
import CastIcon from '@mui/icons-material/Cast';
import TwitchIcon from '../../Components/Icons/Twitch';
import useAlertDialog from '../../Components/useAlertDialog';
import AlertDialog from '../../Components/AlertDialog';
import defaultPfp from '../../Components/globalAssets/ppfLogo.png';

// Use a darker theme for celebrity creators.
const getAppropriateTheme = (creator) => {
  if (creator && creator.creator && creator.creator.tier_level === 'celebrity') {
    return darkTheme;
  }
  return lightTheme;
};

const StyledRating = styled(Rating)(({ theme }) => ({
  '& .MuiRating-iconFilled': {
    color: '#fff',
  },
  '& .MuiRating-iconHover': {
    color: '#ddd',
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  width: '5.5em',
  background: 'linear-gradient(45deg, #646AFF, #7B2A2A)',
  color: '#FFF',
  '&:hover': {
    background: 'linear-gradient(45deg, #747AFF, #8B3A3A)',
  },
}));

const BookButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, red, orange, yellow, green, blue, indigo, violet)',
  backgroundSize: '400% 400%',
  color: '#FFF',
  borderRadius: '23px',
  fontWeight: '700',
  height: '46px',
  transition: 'transform 0.3s ease-in-out',
  animation: 'rainbow 6s ease infinite',
  '&:hover': {
    transform: 'scale(1.1)',
  },
  '@keyframes rainbow': {
    '0%': {
      backgroundPosition: '0% 50%',
    },
    '50%': {
      backgroundPosition: '100% 50%',
    },
    '100%': {
      backgroundPosition: '0% 50%',
    },
  },
}));

// Promotional header styling (if used)
const StylePromotionalHeader = {
  '@keyframes slideDown': {
    '0%': {
      transform: 'translateY(-200%)',
    },
    '100%': {
      transform: 'translateY(0)',
    }
  },
  backgroundColor: '#ff9900',
  color: '#fff',
  textAlign: 'center',
  padding: '5px',
  fontSize: '1.5em',
  fontWeight: 'bold',
  animation: 'slideDown 1s ease-out',
  position: 'fixed',
  top: '70px',
  width: '100%',
  zIndex: 10,
  textAlign: 'center'
};

const AudienceCardsData = [
  { label: 'Youtube', followingKey: 'youtube', linkKey: 'youtube_link', icon: <YoutubeIcon style={{ verticalAlign: "middle" }} /> },
  { label: 'Instagram', followingKey: 'instagram', linkKey: 'instagram_link', icon: <InstagramIcon style={{ verticalAlign: "middle" }} /> },
  { label: 'TikTok', followingKey: 'tiktok', linkKey: 'tiktok_link', icon: <TikTokIcon style={{ verticalAlign: "middle" }} /> },
  { label: 'Podcasts', followingKey: 'follow_podcast', linkKey: 'podcast_link', icon: <PodcastsIcon style={{ verticalAlign: "middle" }} /> },
  { label: 'Twitch', followingKey: 'twitch', linkKey: 'twitch_link', icon: <TwitchIcon style={{ verticalAlign: "middle" }} /> },
  { label: 'X', followingKey: 'follow_x', linkKey: 'x_link', icon: <TwitterIcon style={{ verticalAlign: "middle" }} /> },
  { label: 'Kick', followingKey: 'follow_kick', linkKey: 'kick_link', icon: <CastIcon style={{ verticalAlign: "middle" }} /> }
];

const platformColors = {
  TikTok: "#222",
  Instagram: "#833AB4",
  Youtube: "#EE0022",
  X: "#00acee",
  Kick: "#00e701",
  Podcasts: "#1DB954",
  Twitch: "#6441a5"
};

function EngagementData({ engagement }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <StatsCard icon={engagement.icon} label={engagement.label} data={engagement.value === 0 ? 'N/A' : engagement.value.toLocaleString()} />
    </Grid>
  );
}

export default function MediaKit() {
  const { creatorId, discount } = useParams();
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const { dialogState, openDialog, closeDialog } = useAlertDialog();
  const navigate = useNavigate();

  // Fetch creator details.
  const { data: creator, error: creatorError, isLoading: creatorLoading } = useQuery({
    queryKey: ['creatorDetails', creatorId],
    queryFn: () => API.creators.fetchDetails(creatorId),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    enabled: !!creatorId,
  });

  useEffect(() => {
    if (creatorError) {
      openDialog(
        'Error',
        <>Error while loading creator:<br />{creatorError?.response?.data?.error || creatorError.message}</>,
        closeDialog,
        closeDialog,
        'Ok',
        null
      );
    }
  }, [creatorError]);

  useEffect(() => {
    if (creator) {
      setBookingDialogOpen(true);
    }
  }, [creator]);

  // Calculate total followers across platforms.
  const totalFollowers = useMemo(() => {
    if (!creator) return 0;
    return platforms.reduce((prev, platform) => {
      return prev + parseInt(creator.creator[platformToFollowing[platform]] || '0', 10);
    }, 0);
  }, [creator]);

  // For non-celebrity creators we normally display rates – but for celebrity, we omit them.
  const isCelebrity = creator && creator.creator && creator.creator.tier_level === 'celebrity';
  const rateList = useMemo(() => platformRatesListFromCreator(creator?.creator), [creator]);

  // Map audience and engagement data.
  const audienceList = useMemo(() => mapPlatformsToAudience(AudienceCardsData, creator?.creator), [creator]);
  const engagementList = useMemo(() => mapPlatformsToEngagement(AudienceCardsData, creator?.creator), [creator]);

  // Demo data for country and gender cards.
  const countryDemos = useMemo(() => {
    if (!creator || !creator.creator.public) return [];
    return creator.creator.public.reduce((acc, demo) => {
      demo.country_data.forEach((entry) => {
        const found = acc.find((c) => c.code === entry.code);
        if (found) {
          found.value += entry.value;
        } else {
          acc.push({ code: entry.code, value: entry.value });
        }
      });
      return acc;
    }, []).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [creator]);

  const genderDemos = useMemo(() => {
    if (!creator || !creator.creator.public) return [];
    const allData = creator.creator.public.flatMap(demo => demo.audience_data);
    return [
      {
        name: 'Male',
        value: allData.reduce((sum, d) => d.gender.toLowerCase() === 'male' ? sum + d.value : sum, 0)
      },
      {
        name: 'Female',
        value: allData.reduce((sum, d) => d.gender.toLowerCase() === 'female' ? sum + d.value : sum, 0)
      }
    ];
  }, [creator]);

  // For overall ratings (if needed).
  const avgReview = creator?.average_rating || 0;
  const stars = Math.round(avgReview);

  // Use a dark theme if celebrity, otherwise light theme.
  const appliedTheme = getAppropriateTheme(creator);
  function RateCard({ rate, label }) {
    return (
        <ListItem disableGutters secondaryAction={<GradientButton>${rate.toLocaleString()}</GradientButton>}>
            <ListItemText primary={label}></ListItemText>
        </ListItem>)
}

  return (
    <ThemeProvider theme={appliedTheme}>
      <CssBaseline />
      <HomeHeader />
      <AlertDialog alertState={dialogState} />
      <Toolbar />
      {discount && (
        <Box sx={StylePromotionalHeader}>
          <Typography variant="h4">BOOK TODAY FOR {discount}% OFF!</Typography>
        </Box>
      )}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer - 1 }} open={creatorLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <PageTitle title={`Book @${creatorId} today - Blitz`} />
      {/* Main container with conditional dark mode modifications for celebrity */}
      <Box
        id="non-scroll-bg"
        sx={(theme) => ({
          minHeight: { xs: 'calc(100vh - 56px)', md: '80vh' },
          top: '56px',
          width: '100%',
          position: { xs: 'static', md: 'sticky' },
          background: isCelebrity
            ? 'linear-gradient(135deg, #0d0d0d, #1a1a1a)'
            : 'linear-gradient(#646AFF, #7B2A2A)',
          zIndex: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 1,
        })}
      >
        <Box
          id="creator-header"
          sx={{
            maxWidth: { xs: '100%', md: 1200 },
            width: 1200,
            overflow: 'hidden',
            mx: 'auto',
            my: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            p: { xs: 2, md: 0 },
            gap: 4,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              minWidth: 300,
              maxWidth: 300,
              gap: 2,
              mx: 'auto',
            }}
          >
            <Box sx={{ height: 300, width: '100%' }}>
              <img
                src={creator?.creator.pfphref || defaultPfp}
                style={{ height: '100%', width: '100%', objectFit: 'cover', borderRadius: '6px' }}
                alt="Profile"
              />
            </Box>
            <BookButton onClick={() => setBookingDialogOpen(true)}>Book me!</BookButton>
          </Box>
          <Box
            sx={{
              color: isCelebrity ? '#fff' : '#000',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              flex: 1,
              p: 2,
            }}
          >
            <Box>
              <Typography variant="h3" sx={{ wordBreak: 'break-all' }}>
                {creatorId}
              </Typography>
              {creator?.creator.is_verified && (
                <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                  This creator is verified <CheckCircleIcon sx={{ ml: 1, color: isCelebrity ? '#fff' : '#000' }} />
                </Typography>
              )}
              <Typography variant="h6" sx={{ mt: 1 }}>
                <PlaceIcon fontSize="inherit" style={{ verticalAlign: "middle" }} />{' '}
                {creator ? (
                  <>
                    {creator?.creator.geolocation} ({creator?.creator.region})
                  </>
                ) : (
                  <Skeleton animation="wave" width="100%" />
                )}
              </Typography>
              {/* Instead of a rate or market button, show primary markets minimally */}
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body1">
                  {creator ? creator?.creator.primary_markets?.join(', ') || 'N/A' : <Skeleton animation="wave" width="100%" />}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {creator ? creator?.creator.notes_content_style : <Skeleton animation="wave" height="5em" />}
              </Typography>
            </Box>
            <Box>
              <Typography variant="h6">Total Followers:</Typography>
              <Typography variant="h5">{totalFollowers.toLocaleString()}</Typography>
            </Box>
          </Box>
          {/* For non-celebrity creators, display reviews; for celebrity, show minimal audience icons */}
          <Box sx={{ alignSelf: 'flex-start', display: 'flex', flexDirection: 'row', gap: '0.5em', color: isCelebrity ? '#fff' : '#000' }}>
            {!isCelebrity && (
              <>
                <StyledRating value={stars} onClick={() => setReviewDialogOpen(true)} readOnly />
                <Typography variant="body2">({creator?.review_count || 0})</Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
      <Paper 
        square 
        elevation={isCelebrity ? 0 : 1} 
        sx={{ 
          width: '100%', 
          p: 2, 
          mt: 3, 
          position: 'relative', 
          backgroundColor: isCelebrity ? '#1a1a1a' : undefined, 
          color: isCelebrity ? '#fff' : undefined 
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2}>
              {audienceList.map((audience, index) => (
                <Grid item xs={6} md={4} key={index}>
                  <PlatformCard
                    key={audience.link}
                    handle={audience.handle}
                    link={audience.link}
                    following={audience.following}
                    views={audience.average_views}
                    icon={audience.icon}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
          {/* Conditionally render Rates section only for non-celebrity creators */}
          {!isCelebrity && (
            <>
              <Typography variant="h6">Rates</Typography>
              <Divider></Divider>
                    <List>
                        {rateList.map((rate) => <RateCard label={rate.label} rate={rate.rate} key={rate.label}></RateCard>)}
                    </List>
            </>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              mt: 2
            }}
          >
            <Typography variant="h6">Platform Statistics</Typography>
            <ToggleButtonGroup
              value={selectedPlatform}
              exclusive
              onChange={(event, newState) => setSelectedPlatform(newState)}
            >
              {getPlatformEngagement(creator?.creator, 'TikTok') && (
                <ToggleButton value="TikTok">
                  <TikTokIcon />
                </ToggleButton>
              )}
              {getPlatformEngagement(creator?.creator, 'Instagram') && (
                <ToggleButton value="Instagram">
                  <InstagramIcon />
                </ToggleButton>
              )}
              {getPlatformEngagement(creator?.creator, 'Youtube') && (
                <ToggleButton value="Youtube">
                  <YoutubeIcon />
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </Box>
          <Divider sx={{ mt: 2 }} />
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {engagementList.map((engagement, index) => (
                <EngagementData key={`engagement-${index}`} engagement={engagement} />
              ))}
              <Grid item xs={12} sm={6} md={4}>
                <CountryCard data={countryDemos} fill={selectedPlatform ? platformColors[selectedPlatform] : '#646AFF'} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <GenderCard data={genderDemos} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <AgeCard data={[]} fill={[]} />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Paper>
      <BookingDialog
        open={bookingDialogOpen}
        onClose={() => setBookingDialogOpen(false)}
        creator={creator?.creator}
        isCelebrity={isCelebrity}   // ← passes celebrity status
      />
      <LeaveReview
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        creatorId={creatorId}
      />
    </ThemeProvider>
  );
}
